import type { CareGap } from "../../../../../api/care-gaps";
import { populationCodeTooltips } from "../../../shared-components/fhir-sections/caregaps/shared";
import { formatDate, formatPeriod } from "../../utils/date-format-utils";
import {
  calculateCareGapStatus,
  calculateGroupStatus,
  formatPopulationCode,
  formatStatus,
  formatPascalCase,
  getStatusColor,
} from "./care-gaps-utils";

export type FormattedPopulation = {
  code: string;
  formattedCode: string;
  hasCount: boolean;
  tooltipText?: string;
};

export type FormattedGroup = {
  id: string;
  code: string;
  formattedCode: string;
  status: "completed" | "open" | "ineligible" | "excluded";
  formattedStatus: string;
  statusColor: string;
  populations: FormattedPopulation[];
};

export type CareGapRowData = {
  id: string;
  measureName: string;
  formattedMeasureName: string;
  status: "completed" | "open" | "ineligible" | "excluded";
  formattedStatus: string;
  statusColor: string;
  period: string;
  formattedLastRun: string;
  formattedGroups: FormattedGroup[];
  originalData: CareGap;
};

/**
 * Generate table rows for care gaps section using API data
 */
export function getCareGapRows({ careGaps }: { careGaps?: CareGap[] }): CareGapRowData[] {
  return (
    careGaps?.map(careGap => {
      const { start, end } = careGap.measureReport.period;
      const periodFormatted = formatPeriod(start, end);
      const formattedLastRun = formatDate(careGap.lastRun);
      const calculatedStatus = calculateCareGapStatus(careGap.measureReport);
      const formattedStatusValue = formatStatus(calculatedStatus);

      const firstGroupCode =
        careGap.measureReport.group?.[0]?.code.coding?.[0]?.code || careGap.measureName;
      const formattedMeasureNameValue = formatPascalCase(firstGroupCode);

      // Pre-compute all formatted group and population data
      const formattedGroupsData: FormattedGroup[] =
        careGap.measureReport.group?.map((group, groupIndex) => {
          const groupCode = group.code.coding?.[0]?.code || group.id || `Group ${groupIndex + 1}`;
          const groupStatus = calculateGroupStatus(group);

          const formattedPopulations: FormattedPopulation[] =
            group.population?.map((pop, popIndex) => {
              const popCode = pop.code.coding?.[0]?.code || `Population ${popIndex + 1}`;
              const hasCount = pop.count !== undefined && pop.count > 0;
              const formattedPopCode = formatPopulationCode(popCode);
              const tooltipText = populationCodeTooltips[popCode.toLowerCase()];

              return {
                code: popCode,
                formattedCode: formattedPopCode,
                hasCount,
                tooltipText,
              };
            }) || [];

          return {
            id: group.id || `group-${groupIndex}`,
            code: groupCode,
            formattedCode: groupCode,
            status: groupStatus,
            formattedStatus: formatStatus(groupStatus),
            statusColor: getStatusColor(groupStatus),
            populations: formattedPopulations,
          };
        }) || [];

      return {
        id: careGap.id,
        measureName: careGap.measureName,
        formattedMeasureName: formattedMeasureNameValue,
        status: calculatedStatus,
        formattedStatus: formattedStatusValue,
        statusColor: getStatusColor(calculatedStatus),
        period: periodFormatted,
        formattedLastRun,
        formattedGroups: formattedGroupsData,
        originalData: careGap,
      };
    }) || []
  );
}
